import { useState, useEffect, useCallback } from "react";
import { Eye, ChevronDown } from "lucide-react";
import axios from "axios";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { API } from "../../App";
import { format } from "date-fns";
import { toast } from "sonner";
import { motion } from "framer-motion";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      let url = `${API}/admin/orders`;
      if (statusFilter !== "all") {
        url += `?status=${statusFilter}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      packed: "status-packed",
      shipped: "status-shipped",
      delivered: "status-delivered",
      cancelled: "status-cancelled",
    };
    return statusMap[status] || "status-pending";
  };

  const statusOptions = [
    "pending",
    "confirmed",
    "packed",
    "shipped",
    "delivered",
    "cancelled",
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-orders">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Orders</h1>
            <p className="text-muted-foreground mt-1">Manage customer orders</p>
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-40 bg-card border-white/10"
              data-testid="status-filter"
            >
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              {statusOptions.map((status) => (
                <SelectItem key={status} value={status} className="capitalize">
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-card rounded-xl"></div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 bg-card border border-white/5 rounded-xl">
            <p className="text-muted-foreground">No orders found</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-card border border-white/5 rounded-xl overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.order_id}
                      data-testid={`order-row-${order.order_id}`}
                    >
                      <td className="font-mono text-sm">{order.order_id}</td>
                      <td>
                        <p className="font-medium text-foreground">
                          {order.shipping_address?.full_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {order.shipping_address?.city}
                        </p>
                      </td>
                      <td>{order.items?.length} item(s)</td>
                      <td className="font-medium">₹{order.total}</td>
                      <td>
                        <span
                          className={`text-xs ${
                            order.payment_status === "paid"
                              ? "text-green-500"
                              : "text-yellow-500"
                          }`}
                        >
                          {order.payment_status}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusClass(
                            order.order_status
                          )}`}
                        >
                          {order.order_status}
                        </span>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {formatDate(order.created_at)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedOrder(order)}
                            data-testid={`view-order-${order.order_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-white/10"
                              >
                                Status <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {statusOptions.map((status) => (
                                <DropdownMenuItem
                                  key={status}
                                  onClick={() =>
                                    updateOrderStatus(order.order_id, status)
                                  }
                                  className="capitalize"
                                >
                                  {status}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setSelectedOrder(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-white/10 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="order-detail-modal"
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-foreground">
                    {selectedOrder.order_id}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(selectedOrder.created_at)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedOrder(null)}
                >
                  ✕
                </Button>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">
                    Shipping Address
                  </h3>
                  <div className="text-sm text-foreground">
                    <p className="font-medium">
                      {selectedOrder.shipping_address?.full_name}
                    </p>
                    <p>{selectedOrder.shipping_address?.phone}</p>
                    <p>{selectedOrder.shipping_address?.address_line1}</p>
                    {selectedOrder.shipping_address?.address_line2 && (
                      <p>{selectedOrder.shipping_address?.address_line2}</p>
                    )}
                    <p>
                      {selectedOrder.shipping_address?.city},{" "}
                      {selectedOrder.shipping_address?.state} -{" "}
                      {selectedOrder.shipping_address?.pincode}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">
                    Order Info
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      Payment:{" "}
                      <span className="text-foreground capitalize">
                        {selectedOrder.payment_method}
                      </span>
                    </p>
                    <p>
                      Payment Status:{" "}
                      <span
                        className={
                          selectedOrder.payment_status === "paid"
                            ? "text-green-500"
                            : "text-yellow-500"
                        }
                      >
                        {selectedOrder.payment_status}
                      </span>
                    </p>
                    <p>
                      Order Status:{" "}
                      <span className="capitalize text-foreground">
                        {selectedOrder.order_status}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Items
              </h3>
              <div className="space-y-3 mb-6">
                {selectedOrder.items?.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex justify-between items-center p-3 bg-background rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-foreground">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.sku} • Qty: {item.quantity}
                      </p>
                    </div>
                    <p className="font-medium text-foreground">
                      ₹{item.subtotal}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/5 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹{selectedOrder.subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>
                    {selectedOrder.shipping_fee === 0
                      ? "FREE"
                      : `₹${selectedOrder.shipping_fee}`}
                  </span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="text-green-500">
                      -₹{selectedOrder.discount}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/5">
                  <span>Total</span>
                  <span>₹{selectedOrder.total}</span>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;